<template>
    <div class="position-relative" :class="{ 'mt-6': shouldDivide}">
        <div class="divider" v-if="shouldDivide"><span class="divider-heading fw-bold text-sm">{{ dividerHeading }}</span></div>

        <div class="mb-4 row">
            <div class="col-45px">
                <div class="rounded-circle float-right" :style="userImage"></div>
            </div>
            <div class="col-12-45px">
                <div class="ps-2 pe-3">
                    <p class="m-0 text-base "><span v-if="!sentByMe && sentByOtherThanMainUser" :title="getSentByOtherUserInfo">
                        <svg xmlns="http://www.w3.org/2000/svg" style="height: 18px; width: 18px; margin-bottom: 3px; margin-right:3px;" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </span><strong>{{ senderName }}</strong>
                        <span class="dot bg-elev-blue-dim"></span> <span class="text-sm">{{ sentAt }}</span></p>

                    <div class="text-primary-darkest m-0" v-html="getMessage">
                    </div>

                    <div v-if="hasAttachments" class="row mx-n1" >
                        <div v-for="attachment in attachments" class="col-sm-4 p-1">
                            <a :href="getAttachmentLink(attachment.id)" target="_blank"  style="border: 1px solid #dee2e6; padding: 2px; margin-right: 2px;" class="rounded d-block">
                                <img v-if="isImage(attachment)" :src="getAttachmentPath(attachment.id, attachment.uri_name)" :alt="attachment.name" class="img-thumbnail" @load="imageLoaded" loading=lazy>
                                <div v-if="!isImage(attachment)" class="img-thumbnail text-center text-black-50">
                                    <span class="far fa-file-alt fa-3x"></span>
                                </div>
                                <div class="text-center">
                                    <small>{{attachment.name}}</small>
                                </div>
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed } from 'vue';
import * as linkify from 'linkifyjs';
import linkifyHtml from 'linkify-html';
import Store from '../../state/chatStore';

const props = defineProps(['message', 'conversation', 'previousMessage']);
const emit = defineEmits(['loaded']);

const sentByMe = computed(() => props.message.type === 'IN_CHAT');
const sentByOtherThanMainUser = computed(() => props.conversation.teacher_id !== null && props.message.from_user_id !== props.conversation.teacher_id);
const getSentByOtherUserInfo = computed(() => 'Denne meldingen er sendt av ' + senderName.value + ', ikke ' + Store.getTeacher(props.conversation.teacher_id).name);
const hasAttachments = computed(() => props.message.attachments && props.message.attachments.length > 0);
const getMessage = computed(() => linkifyHtml(props.message.message, {
    target: "_blank",
    className: "text-primary-darkest text-decoration-underline",
}));
const attachments = computed(() => {
    if (!hasAttachments.value) {
        return [];
    }
    return props.message.attachments.split(';').map(attachment => {
        const parts = attachment.split(',');
        return {
            name: parts[0],
            uri_name: encodeURI(parts[0]),
            id: encodeURI(parts[1]),
        }
    });
});
const teacher = computed(() => Store.getTeacher(props.message.from_user_id));
const senderName = computed(() => sentByMe.value ? Store.getUser().name : teacher.value.name);
const showUserImage = computed(() => sentByMe.value ? Store.getUser().image : teacher.value.user_image);
const userImage = computed(() => {
    let userImage = window.enforceCDN('/web/resources/img/man_stilo_150.png');
    if (showUserImage.value) {
        userImage = sentByMe.value ? Store.getUser().image : window.enforceCDN('/img/ansatte/' + teacher.value.user_image);
    }
    return {
        width: '32px',
        height: '32px',
        backgroundImage: 'url("' + userImage + '")',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundColor: '#fff',
        border: '1px solid #B1BAC7',
    };
});
const sentAt = computed(() => props.message.formattedSentTime);
const shouldDivide = computed(() => !props.previousMessage || props.previousMessage.dividerHeading !== props.message.dividerHeading);
const dividerHeading = computed(() => props.message.dividerHeading);

const isImage = (attachment) => {
    const filenameParts = attachment.name.split('.');
    return ['jpg', 'jpeg', 'png'].indexOf(filenameParts[filenameParts.length - 1]) > -1;
};
const getAttachmentPath = (id, name) => '/messages/attachment?fileId=' + id + '&fileName=' + name + '&messageId=' + props.message.id;
const getAttachmentLink = (id) => '/document/' + id + (Store.isApp() ? '?auth=' + Store.getHashedKey() : '');
const imageLoaded = () => emit('loaded');
</script>

<style scoped>
    .img-thumbnail {
        cursor: zoom-in;
    }
    .dot {
        width: 2px;
        height: 2px;
        border-radius: 2px;
        display: inline-block;
        margin: 4px 2px;
    }
    .col-45px {
        flex: 0 0 45px;
        max-width: 45px;
    }
    .col-12-45px {
        flex: 0 0 calc(100% - 45px);
        max-width: calc(100% - 45px);
    }
    .divider {
        position: absolute;
        top: -2.1rem;
        width: 100vw;
        left: -20px;
        display: block;
        height: 1px;
        border: 1px solid #DCE0E6;
    }
    .divider-heading {
        text-transform: uppercase;
        position: absolute;
        top: -1.1rem;
        left: 57px;
        display: block;
        background: white;
        padding: 8px;
        color: #7C889B;
    }
    @media (min-width: 992px) {
        .divider {
            max-width: 634px;
        }
    }
</style>